// src/pages/reports/DmsUploadReport.js
import React, { useEffect, useState, useMemo } from "react";
import Layout from "../../components/Layout";
import { db } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// --- PREMIUM UI TOKENS (Standardized for Reports) ---
const UI_THEME = {
  bg: "#f8fafc",
  surface: "#ffffff",
  primary: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)",
  secondary: "#f1f5f9",
  accent: "#10b981",
  text: "#0f172a",
  muted: "#64748b",
  border: "#e2e8f0",
  shadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
};

const filterCardStyle = {
  background: UI_THEME.surface,
  padding: "24px",
  borderRadius: "16px",
  boxShadow: UI_THEME.shadow,
  border: `1px solid ${UI_THEME.border}`,
  display: "flex",
  gap: "20px",
  alignItems: "flex-end",
  marginBottom: "32px",
  flexWrap: "wrap",
};

const inputGroupStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const labelStyle = {
  fontSize: "12px",
  fontWeight: 800,
  color: UI_THEME.muted,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const inputStyle = {
  padding: "10px 14px",
  borderRadius: "10px",
  border: `1px solid ${UI_THEME.border}`,
  fontSize: "14px",
  fontWeight: 600,
  color: UI_THEME.text,
  outline: "none",
  minWidth: "160px",
};

const primaryBtn = {
  padding: "10px 24px",
  borderRadius: "10px",
  background: UI_THEME.primary,
  color: "#fff",
  border: "none",
  fontWeight: 700,
  fontSize: "14px",
  cursor: "pointer",
  boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.4)",
};

const secondaryBtn = {
  padding: "10px 20px",
  borderRadius: "10px",
  background: "#f1f5f9",
  color: "#475569",
  border: "none",
  fontWeight: 700,
  fontSize: "14px",
  cursor: "pointer",
};

const tableHeaderStyle = {
  padding: "16px 20px",
  background: "#f8fafc",
  color: "#64748b",
  fontSize: "11px",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  borderBottom: `2px solid ${UI_THEME.border}`,
  textAlign: "left",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const tableCellStyle = {
  padding: "14px 20px",
  fontSize: "14px",
  color: UI_THEME.text,
  borderBottom: `1px solid #f1f5f9`,
};

function toStartOfDayTs(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(x);
}
function toEndOfDayTs(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return Timestamp.fromDate(x);
}

function formatDDMMYYYY(ts) {
  return ts.toDate().toLocaleDateString("en-IN");
}

function formatQty(v) {
  return Number(v || 0).toLocaleString(undefined, { 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 3,
    useGrouping: false 
  });
}

export default function DmsUploadReport() {
  const today = new Date().toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [partyFilter, setPartyFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadReport = async () => {
    try {
      setLoading(true);

      let q = query(
        collection(db, "orderLines"),
        where("status", "==", "APPROVED"),
        where("approvedAt", ">=", toStartOfDayTs(fromDate)),
        where("approvedAt", "<=", toEndOfDayTs(toDate))
      );

      const snap = await getDocs(q);
      const map = new Map();

      snap.forEach((doc) => {
        const r = doc.data();
        if (partyFilter && r.partyName !== partyFilter) return;

        const date = formatDDMMYYYY(r.approvedAt);
        const key = `${date}__${r.dmsName}__${r.msku}`;

        if (!map.has(key)) {
          map.set(key, {
            Date: date,
            DMSName: r.dmsName || "",
            MSKU: r.msku || "",
            Qty: 0,
          });
        }
        map.get(key).Qty += Number(r.qty || 0);
      });

      setRows(Array.from(map.values()));
    } catch (e) {
      console.error(e);
      alert("Failed to load DMS Upload Report");
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DMS");
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([out]), `DMS_Upload_${fromDate}_to_${toDate}.xlsx`);
  };

  return (
    <Layout>
      <div style={{ padding: "32px", background: UI_THEME.bg, minHeight: "calc(100vh - 80px)" }}>
        
        {/* PAGE HEADER */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "32px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: 800, color: UI_THEME.text, margin: 0 }}>DMS Upload Report</h1>
            <p style={{ color: UI_THEME.muted, fontWeight: 500, marginTop: "4px" }}>Synchronized sales data for platform integration</p>
          </div>
          <button onClick={exportExcel} style={secondaryBtn}>
            📥 Export to Excel
          </button>
        </div>

        {/* FILTER CARD */}
        <div style={filterCardStyle}>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Date From</label>
            <input type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={inputGroupStyle}>
            <label style={labelStyle}>Date To</label>
            <input type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ ...inputGroupStyle, flex: 1, minWidth: "250px" }}>
            <label style={labelStyle}>Party Name (Optional)</label>
            <input 
              placeholder="Filter by party..." 
              onChange={(e)=>setPartyFilter(e.target.value)} 
              style={{ ...inputStyle, width: "100%" }} 
            />
          </div>
          <button onClick={loadReport} style={primaryBtn}>Load Data</button>
        </div>

        {/* DATA TABLE CARD */}
        <div style={{ 
          background: UI_THEME.surface, 
          borderRadius: "16px", 
          boxShadow: UI_THEME.shadow, 
          border: `1px solid ${UI_THEME.border}`,
          overflow: "hidden"
        }}>
          <div style={{ overflowX: "auto", maxHeight: "600px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Date</th>
                  <th style={tableHeaderStyle}>DMS Name</th>
                  <th style={tableHeaderStyle}>MSKU</th>
                  <th style={{ ...tableHeaderStyle, textAlign: "center" }}>Qty</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" style={{ padding: "100px", textAlign: "center", color: UI_THEME.muted }}>
                      <div style={{ fontSize: "24px", marginBottom: "16px" }}>⏳</div>
                      Preparing DMS sync data...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ padding: "100px", textAlign: "center", color: UI_THEME.muted }}>
                      <div style={{ fontSize: "24px", marginBottom: "16px" }}>📭</div>
                      No records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r,i)=>(
                    <tr key={i} style={{ 
                      background: i % 2 === 0 ? "#fff" : "#fafafa",
                      transition: "background 0.2s"
                    }}>
                      <td style={tableCellStyle}>{r.Date}</td>
                      <td style={{ ...tableCellStyle, fontWeight: 700 }}>{r.DMSName}</td>
                      <td style={{ ...tableCellStyle, color: "#4f46e5", fontWeight: 800 }}>{r.MSKU}</td>
                      <td style={{ ...tableCellStyle, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>{formatQty(r.Qty)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && rows.length > 0 && (
            <div style={{ padding: "16px 24px", borderTop: `1px solid ${UI_THEME.border}`, background: "#f8fafc" }}>
               <span style={{ fontSize: "13px", fontWeight: 600, color: UI_THEME.muted }}>
                Total Records: {rows.length}
              </span>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
